import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import { type MouseEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import type { AppState } from '@/types'

const useFocusState = (
  disabled: boolean,
  id: string,
  onChangeSelected: (id: string | null) => void
) => {
  const [isSelected, setIsSelected] = useState(false)

  const dispatch = useDispatch()

  const selectedStickyNote = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.stickyNote.selectedStickyNote
  )
  const selectedToEditStickyNote = useSelector<AppState, string | null>(
    ({ canvas }) => canvas.stickyNote.selectedToEditStickyNote
  )

  const isEditing = selectedToEditStickyNote === id

  const select = () => {
    setIsSelected(true)
    onChangeSelected(id)

    dispatch({
      type: CanvasActionTypes.SELECT_STICKY_NOTE,
      payload: id,
    })
  }

  const edit = () => {
    dispatch({
      type: CanvasActionTypes.SELECT_TO_EDIT_STICKY_NOTE,
      payload: id,
    })
  }

  // The first click selects, the second one starts editing; a drag ends with a
  // mouseup too and must do neither.
  const handleMouseUp = (
    _e: MouseEvent<HTMLDivElement>,
    isDragging: boolean
  ) => {
    if (disabled || isDragging || isEditing) {
      return
    }

    if (isSelected) {
      edit()
      return
    }

    select()
  }

  const handleBlur = () => {
    setIsSelected(false)
    onChangeSelected(null)

    if (selectedStickyNote === id) {
      dispatch({
        type: CanvasActionTypes.CLEAR_SELECTED_STICKY_NOTE,
      })
    }
  }

  // @TODO - move to KeyboardListener component
  useEffect(() => {
    const onKeydown = (e: KeyboardEvent) => {
      if (e.code !== 'Escape') {
        return
      }

      e.preventDefault()

      // Escape steps back one level: out of the text first, then off the note.
      if (isEditing) {
        select()
        return
      }

      handleBlur()
    }

    document.addEventListener('keydown', onKeydown)

    return () => {
      document.removeEventListener('keydown', onKeydown)
    }
  }, [selectedStickyNote, isEditing])

  useEffect(() => {
    if (selectedStickyNote === id) {
      return
    }

    setIsSelected(false)
  }, [selectedStickyNote])

  return {
    isActionsOpen: isSelected && !disabled,
    isEditing,
    isSelected,
    handleBlur,
    handleMouseUp,
  }
}

export default useFocusState
